import React, { useState, useEffect } from 'react';
import { getCustomer360DetailApiClient, Customer360Detail } from '../../services/customer360DetailApi';
import { getSupabaseServiceKey } from '../../services/auth';
import { getCurrentUser } from '../../services/auth';
import CustomerChat from './CustomerChat';
import { toast } from '../../hooks/use-toast';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';

export default function Customer360Page() {
  const [searchQuery, setSearchQuery] = useState('');
  const [customers, setCustomers] = useState<Customer360Detail[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer360Detail | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasServiceKey, setHasServiceKey] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    const serviceKey = getSupabaseServiceKey();
    if (!serviceKey) {
      setHasServiceKey(false);
    }
  }, []);

  useEffect(() => {
    if (hasServiceKey) {
      loadCustomers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasServiceKey, page]);

  const loadCustomers = async () => {
    if (!hasServiceKey) return;
    
    setLoading(true);
    try {
      const apiClient = getCustomer360DetailApiClient();
      const response = await apiClient.searchCustomers(searchQuery, page, 20);

      // Garantir que temos dados válidos
      const customers = Array.isArray(response?.data) ? response.data : [];
      
      // Tratar paginação com validação robusta
      let pagination;
      if (response?.pagination && typeof response.pagination === 'object') {
        pagination = {
          page: response.pagination.page || page,
          limit: response.pagination.limit || 20,
          total: response.pagination.total || customers.length,
          totalPages: response.pagination.totalPages || Math.max(1, Math.ceil((response.pagination.total || customers.length) / (response.pagination.limit || 20)))
        };
      } else {
        pagination = {
          page: page,
          limit: 20,
          total: customers.length,
          totalPages: Math.max(1, Math.ceil(customers.length / 20))
        };
      }
      
      setCustomers(customers);
      setTotalPages(pagination.totalPages);
      
      // Selecionar primeiro cliente se houver resultados e nenhum estiver selecionado
      if (customers.length > 0 && !selectedCustomer) {
        setSelectedCustomer(customers[0]);
      } else if (customers.length === 0) {
        setSelectedCustomer(null);
      }
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      toast({
        title: 'Erro ao carregar clientes',
        description: errorMessage,
        variant: 'destructive',
      });
      setCustomers([]);
      setTotalPages(1);
      setSelectedCustomer(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    loadCustomers();
  };

  const handleSelectCustomer = async (customer: Customer360Detail) => {
    setSelectedCustomer(customer);
  };

  const formatCurrency = (value: number | string | null | undefined) => {
    if (value === null || value === undefined || value === '') return 'R$ 0,00';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numValue);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const formatDaysAgo = (dateString: string | null) => {
    if (!dateString) return 'Nunca';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Hoje';
    if (diffDays === 1) return '1 dia atrás';
    return `${diffDays} dias atrás`;
  };

  if (!hasServiceKey) {
    return (
      <div className="mx-auto w-full max-w-6xl space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>Configuração necessária</CardTitle>
            <CardDescription>
              Configure a Supabase Service Key em Settings para visualizar dados de Customer 360.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-7xl space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold leading-tight">Customer 360</h1>
          <p className="text-sm text-muted-foreground">
            👤 {getCurrentUser()?.name || getCurrentUser()?.email?.split('@')[0] || 'Usuário'}
          </p>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle>Buscar lead</CardTitle>
          <CardDescription>Busque por ID, email, nome ou external_id.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Input
            type="text"
            placeholder="Ex.: maria.silva@example.com"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
          <Button onClick={handleSearch} disabled={loading} className="sm:w-40">
            {loading ? 'Buscando...' : 'Buscar'}
          </Button>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[360px_1fr_420px]">
        {/* LISTA */}
        <Card className="h-[calc(100vh-240px)] min-h-[560px]">
          <CardHeader className="pb-3">
            <CardTitle>Clientes {customers.length > 0 ? `(${customers.length})` : ''}</CardTitle>
            <CardDescription>
              {loading && customers.length === 0 ? 'Carregando...' : 'Selecione um cliente para ver detalhes.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="h-[calc(100%-96px)]">
            {customers.length === 0 && !loading ? (
              <div className="text-sm text-muted-foreground">Nenhum cliente encontrado</div>
            ) : (
              <ScrollArea className="h-full pr-2">
                <div className="space-y-2">
                  {customers.map((customer) => {
                    const isSelected = selectedCustomer?.id === customer.id;
                    return (
                      <button
                        key={customer.id}
                        type="button"
                        onClick={() => handleSelectCustomer(customer)}
                        className={[
                          'w-full rounded-lg border p-3 text-left transition',
                          isSelected ? 'border-primary/50 bg-primary/5' : 'hover:bg-accent',
                        ].join(' ')}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="truncate font-medium">
                              {customer.full_name || customer.email || 'Sem nome'}
                            </div>
                            <div className="truncate text-xs text-muted-foreground">
                              {customer.email || 'Sem email'}
                            </div>
                          </div>
                          <Badge variant={customer.is_active ? 'default' : 'secondary'}>
                            {customer.is_active ? 'Ativo' : 'Inativo'}
                          </Badge>
                        </div>

                        <div className="mt-2 flex items-center justify-between gap-2 text-xs">
                          <span className="truncate text-muted-foreground">
                            {customer.segment_label || customer.lifecycle_stage || '—'}
                          </span>
                          <span className="font-medium">LTV: {formatCurrency(customer.net_revenue)}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </ScrollArea>
            )}

            {totalPages > 1 ? (
              <>
                <Separator className="my-3" />
                <div className="flex items-center justify-between gap-2">
                  <Button variant="outline" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                    Anterior
                  </Button>
                  <span className="text-xs text-muted-foreground">
                    Página {page} de {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    Próxima
                  </Button>
                </div>
              </>
            ) : null}
          </CardContent>
        </Card>

        {/* DETALHES */}
        <div className="space-y-4">
          {!selectedCustomer ? (
            <Card className="h-[calc(100vh-240px)] min-h-[560px]">
              <CardContent className="flex h-full items-center justify-center text-sm text-muted-foreground">
                Selecione um cliente na lista para ver os detalhes
              </CardContent>
            </Card>
          ) : (
            <>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center justify-between gap-3">
                    <span className="truncate">
                      {selectedCustomer.full_name || selectedCustomer.email || 'Cliente sem nome'}
                    </span>
                    <Badge variant={selectedCustomer.is_active ? 'default' : 'secondary'}>
                      {selectedCustomer.is_active ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </CardTitle>
                  <CardDescription className="truncate">{selectedCustomer.email || '—'}</CardDescription>
                </CardHeader>
                <CardContent className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div className="text-sm">
                    <div className="text-xs text-muted-foreground">ID</div>
                    <div className="font-medium">{selectedCustomer.id}</div>
                  </div>
                  <div className="text-sm">
                    <div className="text-xs text-muted-foreground">Telefone</div>
                    <div className="font-medium">{selectedCustomer.phone || '—'}</div>
                  </div>
                  <div className="text-sm">
                    <div className="text-xs text-muted-foreground">Segmento</div>
                    <div className="font-medium">{selectedCustomer.segment_label || '—'}</div>
                  </div>
                  <div className="text-sm">
                    <div className="text-xs text-muted-foreground">Lifecycle</div>
                    <div className="font-medium">{selectedCustomer.lifecycle_stage || '—'}</div>
                  </div>
                  <div className="text-sm">
                    <div className="text-xs text-muted-foreground">Tier</div>
                    <div className="font-medium">{selectedCustomer.tier ? selectedCustomer.tier.toUpperCase() : '—'}</div>
                  </div>
                  <div className="text-sm">
                    <div className="text-xs text-muted-foreground">Localização</div>
                    <div className="font-medium">
                      {selectedCustomer.city && selectedCustomer.uf
                        ? `${selectedCustomer.city}, ${selectedCustomer.uf}`
                        : '—'}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle>KPIs</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-3 lg:grid-cols-3">
                  <div className="rounded-lg border p-3">
                    <div className="text-xs text-muted-foreground">LTV</div>
                    <div className="text-lg font-semibold">{formatCurrency(selectedCustomer.net_revenue)}</div>
                  </div>
                  <div className="rounded-lg border p-3">
                    <div className="text-xs text-muted-foreground">Total Deposit</div>
                    <div className="text-lg font-semibold">{formatCurrency(selectedCustomer.total_deposit)}</div>
                  </div>
                  <div className="rounded-lg border p-3">
                    <div className="text-xs text-muted-foreground">Total Withdraw</div>
                    <div className="text-lg font-semibold">{formatCurrency(selectedCustomer.total_withdraw)}</div>
                  </div>
                  <div className="rounded-lg border p-3">
                    <div className="text-xs text-muted-foreground">Última atividade</div>
                    <div className="text-lg font-semibold">{formatDaysAgo(selectedCustomer.last_activity_at)}</div>
                  </div>
                  <div className="rounded-lg border p-3">
                    <div className="text-xs text-muted-foreground">Churn Score</div>
                    <div className="text-lg font-semibold">
                      {selectedCustomer.churn_score !== null && selectedCustomer.churn_score !== undefined
                        ? Number(selectedCustomer.churn_score).toFixed(1)
                        : '—'}
                    </div>
                  </div>
                  <div className="rounded-lg border p-3">
                    <div className="text-xs text-muted-foreground">Engagement</div>
                    <div className="text-lg font-semibold">
                      {selectedCustomer.engagement_score !== null && selectedCustomer.engagement_score !== undefined
                        ? Number(selectedCustomer.engagement_score).toFixed(1)
                        : '—'}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle>Timeline</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  {selectedCustomer.last_login_at ? (
                    <div>🟢 Login — {formatDaysAgo(selectedCustomer.last_login_at)}</div>
                  ) : null}
                  {selectedCustomer.last_deposit_at ? (
                    <div>
                      💰 Depósito {formatCurrency(selectedCustomer.avg_deposit)} —{' '}
                      {formatDaysAgo(selectedCustomer.last_deposit_at)}
                    </div>
                  ) : null}
                  {selectedCustomer.last_withdraw_at ? (
                    <div>
                      💸 Saque {formatCurrency(selectedCustomer.avg_withdraw)} —{' '}
                      {formatDaysAgo(selectedCustomer.last_withdraw_at)}
                    </div>
                  ) : null}
                  {selectedCustomer.last_campaign_at ? (
                    <div>📣 Última campanha — {formatDaysAgo(selectedCustomer.last_campaign_at)}</div>
                  ) : null}
                  {selectedCustomer.first_activity_at ? (
                    <div>🎯 Primeira atividade — {formatDate(selectedCustomer.first_activity_at)}</div>
                  ) : null}
                  {selectedCustomer.created_at ? (
                    <div>✨ Cliente criado — {formatDate(selectedCustomer.created_at)}</div>
                  ) : null}

                  {!selectedCustomer.last_login_at &&
                  !selectedCustomer.last_deposit_at &&
                  !selectedCustomer.last_withdraw_at &&
                  !selectedCustomer.last_campaign_at ? (
                    <div className="text-muted-foreground">Nenhuma atividade recente</div>
                  ) : null}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle>Informações adicionais</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 gap-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Depósitos</span>
                    <span className="font-medium">{selectedCustomer.deposit_count} transações</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Saques</span>
                    <span className="font-medium">{selectedCustomer.withdraw_count} transações</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Sessões (30d)</span>
                    <span className="font-medium">{selectedCustomer.sessions_last_30d || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Ações (30d)</span>
                    <span className="font-medium">{selectedCustomer.actions_last_30d || 0}</span>
                  </div>
                  {selectedCustomer.acquisition_channel ? (
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Canal de aquisição</span>
                      <span className="font-medium">{selectedCustomer.acquisition_channel}</span>
                    </div>
                  ) : null}
                  {selectedCustomer.persona ? (
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Persona</span>
                      <span className="font-medium">{selectedCustomer.persona}</span>
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            </>
          )}
        </div>

        {/* CHAT */}
        <div className="h-[calc(100vh-240px)] min-h-[560px]">
          {selectedCustomer ? (
            <CustomerChat customer={selectedCustomer} />
          ) : (
            <Card className="h-full">
              <CardContent className="flex h-full items-center justify-center text-sm text-muted-foreground">
                Selecione um cliente para abrir o chat
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
